import { type ReactNode, useRef, useLayoutEffect, useEffect } from 'react';

import { cx } from '@/utils/cx';

import { HintText } from '../input/hint-text';
import { TextArea } from '../textarea/textarea';

function resizeTextAreaToFitContent(el: HTMLTextAreaElement | null) {
  if (!el) return;
  el.style.height = '1px';
  el.style.height = `${el.scrollHeight}px`;
}

export function FormAutoResizeTextarea({
  value,
  onChange,
  onBlur,
  label,
  placeholder,
  isDisabled,
  isInvalid,
  displayHint,
  showError,
  textAreaClassName,
  labelClassName,
  showEmptyHighlight = true,
  className,
  isRequired,
}: {
  value: string;
  onChange: (value: string) => void;
  onBlur: () => void;
  label?: string;
  placeholder?: string;
  isDisabled?: boolean;
  isInvalid?: boolean;
  displayHint?: ReactNode;
  showError?: boolean;
  textAreaClassName?: string;
  labelClassName?: string;
  showEmptyHighlight?: boolean;
  className?: string;
  isRequired?: boolean;
}) {
  const isEmpty = !value.trim();
  const ref = useRef<HTMLTextAreaElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => resizeTextAreaToFitContent(ref.current), [value]);

  useEffect(() => {
    const wrapper = wrapperRef.current;
    const textarea = ref.current;
    if (!wrapper || !textarea) return;

    let lastWidth = wrapper.offsetWidth;
    const ro = new ResizeObserver(() => {
      const w = wrapper.offsetWidth;
      if (w !== lastWidth) {
        lastWidth = w;
        resizeTextAreaToFitContent(textarea);
      }
    });
    ro.observe(wrapper);
    return () => ro.disconnect();
  }, []);

  return (
    <div className={className}>
      {label && (
        <label
          className={cx(
            'flex cursor-default items-center gap-0.5 text-sm font-medium text-secondary',
            labelClassName,
          )}
        >
          {label}
          {isRequired ? <span className="text-brand-tertiary">*</span> : null}
        </label>
      )}
      <div ref={wrapperRef} className={label ? 'mt-1' : undefined}>
        <TextArea
          textAreaRef={ref}
          value={value}
          size="sm"
          onChange={onChange}
          onBlur={onBlur}
          aria-label={label}
          aria-invalid={isInvalid}
          placeholder={placeholder}
          isDisabled={isDisabled}
          className={cx(
            'w-full resize-none overflow-hidden bg-transparent text-sm leading-5.5 placeholder:text-quaternary focus:outline-none',
            isEmpty ? 'text-quaternary' : 'text-primary',
            showEmptyHighlight &&
              isEmpty &&
              'border-s-quaternary border-s-2 ps-2',
            textAreaClassName,
          )}
        />
      </div>
      {displayHint && (
        <HintText isInvalid={showError} className="mt-1">
          {displayHint}
        </HintText>
      )}
    </div>
  );
}
